import React, { useState, useEffect } from 'react'
import { Box, Flex, Group, Button, Select, FocusTrap, NumberInput, Textarea, Text } from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { useSelector } from 'react-redux'
import { addPayment, updatePayment } from '../../../../../services/payment'
import { validation } from '../../../../../plugins/validation'
import { useParams } from 'react-router-dom'
import { notificationSuccess, notificationError } from '../../../../ui/Notifications'
import dayjs from 'dayjs'

const defaultPaymentType = [
  {
    label: 'Payment',
    value: 'payment'
  },
  {
    label: 'Refund',
    value: 'refund'
  },
]

const defaultPaymentMethod = [
  {
    label: 'Bank Transfer',
    value: 'Bank Transfer'
  },
  {
    label: 'Paypal',
    value: 'Paypal'
  },
  {
    label: 'Credit Card',
    value: 'Credit Card'
  }
]

const defaultVal = {
  payment_type: 'payment',
  payment_method: 'Bank Transfer',
  date: new Date(),
  amount: 0,
  currency: '',
  note: ''
}

const formValidation = {
  payment_type: {
    isError: false,
    message: ''
  },
  payment_method: {
    isError: false,
    message: ''
  },
  date: {
    isError: false,
    message: '',
  },
  amount: {
    isError: true,
    message: '',
  },
  currency: {
    isError: true,
    message: '',
  },
}

const FormAdjustPaymentHistory = ({ dataPayment, onCloseForm, reloadList }) => {
  const params = useParams()
  const orderId = params.order_id
  const { summary } = useSelector(state => state.order_detail)
  const [loadingForm, setLoadingForm] = useState(false)
  const [formData, setFormData] = useState(defaultVal)
  const [validationForm, setValidationForm] = useState(formValidation)


  const submitPaymentHistory = async (form) => {
    setLoadingForm(true)
    setValidationForm(formValidation)
    let methodFunction = null
    let titleMessageSuccess = ''
    let captionMessageSuccess = ''
    let titleMessageError = ''
    let captionMessageError = ''
    let payload = {}
    const validateForm = {
      payment_type: form.payment_type,
      payment_method: form.payment_method,
      date: form.date,
      amount: form.amount,
      currency: form.currency,
    }
    
    const isError = validation(validateForm, setValidationForm)
    if (isError) {
      setLoadingForm(false)
      return
    }
    if (dataPayment === null) {
      payload = {
        order_id: orderId,
        label: form.payment_type,
        method: form.payment_method,
        paid_at: dayjs(form.date).format('YYYY-MM-DD'),
        value: Number(form.amount),
        currency: form.currency,
        note: form.note,
      }
      methodFunction = addPayment(payload)
      titleMessageSuccess = 'Add Payment History Successfully'
      captionMessageSuccess = 'You have been added new payment history'
      titleMessageError = 'Add Payment History Error'
    } else {
      payload = {
        order_id: orderId,
        label: form.payment_type,
        method: form.payment_method,
        paid_at: dayjs(form.date).format('YYYY-MM-DD'),
        value: Number(form.amount),
        currency: form.currency,
        note: form.note,
      }
      methodFunction = updatePayment(dataPayment.id, payload)
      titleMessageSuccess = 'Update Payment History Successfully'
      captionMessageSuccess = 'You have been updated data payment history'
      titleMessageError = 'Update Payment History Error'
    }
    try {
      const response = await methodFunction
      if (response.success) {
        onCloseForm()
        reloadList()
        notificationSuccess(titleMessageSuccess, captionMessageSuccess)
      }
    } catch (error) {
      console.log(error)
      const errorMessage = error.response.data.message
      captionMessageError = Object.keys(errorMessage) ? errorMessage : 'Please recheck your form'
      notificationError(titleMessageError, captionMessageError)
    } finally {
      setLoadingForm(false)
    }
  }

  useEffect(() => {
    if (summary) {
      handleChangeForm('currency', summary.orderCurrency)
    }
    if (dataPayment) {
      handleSetForm(dataPayment)
    }
    // eslint-disable-next-line
  }, [dataPayment])

  const handleSetForm = (val) => {
    const detail = {
      id: val.id,
      order_id: val.order_id,
      payment_type: val.label,
      payment_method: val.method,
      date: new Date(val.paid_at),
      amount: val.value,
      currency: val.currency,
      note: val.note
    }
    setFormData(detail)
  }

  const handleChangeForm = (name, val) => {
    setFormData((oldVal) => ({ ...oldVal, [name]: val}))
  }

  return (
    <Box>
      <FocusTrap.InitialFocus />
      <Box mb='sm'>
        <Select
          name='payment_type'
          value={formData.payment_type}
          label='Payment Type'
          placeholder='Select payment type'
          data={defaultPaymentType}
          onChange={(val) => handleChangeForm('payment_type', val)}
          error={validationForm.payment_type.isError ? `${validationForm.payment_type.message}` : ''}
          size='xs'
          allowDeselect={false}
          withAsterisk
        />
      </Box>
      <Box mb='sm'>
        <Select
          name='payment_method'
          value={formData.payment_method}
          label='Payment Method'
          placeholder='Select payment method'
          data={defaultPaymentMethod}
          onChange={(val) => handleChangeForm('payment_method', val)}
          error={validationForm.payment_method.isError ? `${validationForm.payment_method.message}` : ''}
          size='xs'
          allowDeselect={false}
          withAsterisk
        />
      </Box>
      <Box mb='sm'>
        <DateInput
          name='date'
          value={formData.date}
          label='Payment Date'
          placeholder='DD-MM-YYYY'
          valueFormat="DD-MM-YYYY"
          onChange={(val) => handleChangeForm('date', val)}
          error={validationForm.date.isError ? `${validationForm.date.message}` : ''}
          size='xs'
          clearable
          withAsterisk
        />
      </Box>
      <Box mb='sm'>
        <Select
          name='currency'
          value={formData.currency}
          label='Payment Currency'
          placeholder='Select payment currency'
          data={[ summary.orderCurrency ]}
          onChange={(val) => handleChangeForm('currency', val)}
          error={validationForm.currency.isError ? `${validationForm.currency.message}` : ''}
          size='xs'
          allowDeselect={false}
          withAsterisk
        />
      </Box>
      <Box mb='sm'>
        <NumberInput
          name='amount'
          label="Amount"
          value={formData.amount}
          leftSection={<Text fz='xs' c='black' tt='capitalize'>{formData.currency}</Text>}
          placeholder='Input amount'
          onChange={(val) => handleChangeForm('amount', val)}
          error={validationForm.amount.isError ? `${validationForm.amount.message}` : ''}
          size='xs'
          withAsterisk
          thousandSeparator=","
          leftSectionWidth={40}
        />
      </Box>
      <Box mb='sm'>
        <Textarea
          name='note'
          label="Payment Note"
          placeholder="Input payment note"
          value={formData.note}
          onChange={(val) => handleChangeForm('note', val.target.value)}
          size='xs'
          autosize
          minRows={4}
          maxRows={4}
        />
      </Box>
      <Box mt={20}>
        <Flex justify='flex-end'>
          <Group>
            <Button size='xs' variant="outline" onClick={onCloseForm}>Close</Button>
            <Button size='xs' loading={loadingForm} variant="filled" onClick={() => submitPaymentHistory(formData)}>{dataPayment ? 'Update' : 'Create'}</Button>
          </Group>
        </Flex>
      </Box>
    </Box>
  )
}

export default FormAdjustPaymentHistory